/* File: src/components/game/ChallengeExplanationScreen.js */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGame } from '../../contexts/GameContext'; // Import useGame to access startGame and gameState
import { formatCombinedDifficulty } from '../../contexts/gameHelpers'; // Import helper for level display
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForward, faPlay } from '@fortawesome/free-solid-svg-icons'; // Import necessary icons
import LoadingScreen from '../ui/LoadingScreen'; // Import loading screen component for fallback
import { useLoading as useLoadingContextHook } from '../../contexts/LoadingContext'; // For global loading indicator

// Simple Play Icon Component (local definition)
const PlayIcon = () => (
     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="w-3.5 h-3.5 mr-1.5 inline-block" fill="currentColor">
       <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"/>
     </svg>
);

/**
 * ChallengeExplanationScreen Component
 * Displays information about an upcoming N-Back level challenge.
 * Allows the user to start the challenge attempt (which calls startGame with specific flags)
 * or go back to the dashboard.
 */
const ChallengeExplanationScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Get startGame action and current gameState from the GameContext
    const { startGame, gameState } = useGame();
    const { showLoading, hideLoading } = useLoadingContextHook(); // Use global loading indicator

    // Extract the target N-Back level from the navigation state passed by ProfileDashboard
    const nBackToChallenge = location.state?.nBackToChallenge;

    // --- Error Handling / Fallback ---
    // Redirect to dashboard if nBackToChallenge wasn't passed correctly
    if (typeof nBackToChallenge !== 'number' || nBackToChallenge < 1) {
        console.error("ChallengeExplanationScreen: Missing or invalid nBackToChallenge in location state.", location.state);
        // Use effect for safe navigation after render
        React.useEffect(() => {
            navigate('/dashboard', { replace: true });
        }, [navigate]);
        // Render a loading/error message while redirecting
        return <LoadingScreen message="Error determining challenge level..." />;
    }

    // Calculate display strings for levels
    const nextNBackLevel = nBackToChallenge + 1;
    // Challenge is now at N.50 difficulty
    const challengeLevelString = formatCombinedDifficulty(nBackToChallenge, 0.50);
    // Next level starts at minimum difficulty (0.00)
    const nextLevelString = formatCombinedDifficulty(nextNBackLevel, 0.00);

    // --- Action Handlers ---

    // Handler for the "Start Attempt" button
    const handleStartAttempt = async () => {
        // Prevent action if already processing
        if (gameState === 'preparing' || gameState === 'playing') {
            console.warn("ChallengeExplanationScreen: Start attempt clicked while game already preparing/playing.");
            return;
        }

        console.log(`ChallengeExplanationScreen: Starting attempt for Level ${challengeLevelString}`);
        showLoading("Preparing challenge..."); // Show global loading

        // --- Prepare arguments for startGame ---
        // Pass the necessary N-back level and flag it as a challenge attempt
        const startGameArgs = {
            nBack: nBackToChallenge,
            difficulty: 0.50,        // Pass the challenge difficulty explicitly (N.50)
            isChallengeAttempt: true // *** Explicitly pass the flag ***
        };

        // Call the startGame action from GameContext
        if (typeof startGame === 'function') {
            try {
                // Start the game setup process (which is async due to backend call)
                // Awaiting ensures setup is initiated before navigation attempt.
                await startGame(startGameArgs);

                // --- ADD NAVIGATION HERE ---
                // After successfully initiating the game setup (which sets state to 'preparing'),
                // navigate the user to the GameScreen. GameScreen will then observe
                // the 'preparing' state and show the countdown.
                console.log("ChallengeExplanationScreen: startGame initiated. Navigating to /game...");
                navigate('/game');
                // --- END NAVIGATION ---

            } catch (error) {
                 // This catch might not be strictly necessary if startGameLogic handles its own errors
                 // and sets feedback messages, but it's safer.
                 console.error("ChallengeExplanationScreen: Error occurred during startGame call:", error);
                 alert("Error starting the game. Please try again.");
                 hideLoading(); // Hide loading indicator on error
            } finally {
                 // Hide loading indicator once navigation is triggered or if there was an error caught here.
                 // Note: If navigation happens, GameScreen might show its own loading/countdown.
                 // The global indicator can be hidden fairly quickly.
                 // Use a small timeout to ensure navigation potentially starts before hiding.
                 setTimeout(hideLoading, 150); // Slightly increased timeout
            }

        } else {
             console.error("ChallengeExplanationScreen: startGame function not available from GameContext.");
             alert("Error starting the game. Please refresh and try again.");
             hideLoading(); // Hide loading on context function error
        }
    };

    // Handler for the "Maybe Later" or "Back" button
    const handleGoBack = () => {
        // Prevent navigation if game is actively starting
        if (gameState === 'preparing') return;
        navigate('/dashboard'); // Simple navigation back to the dashboard
    };


    // --- Render Component ---
    return (
        // Main Page Container - Centered content, matching dashboard/other pages
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen flex items-center justify-center">
            {/* Content Card - White background, rounded, shadow */}
            <div className="max-w-xl w-full mx-auto bg-white p-6 sm:p-8 rounded-lg shadow-xl border border-gray-200">

                {/* Header Section */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon
                            icon={faForward} // Skip/forward icon
                            className="w-5 h-5 mr-2 text-indigo-600"
                            aria-hidden="true"
                        />
                        Skip {nBackToChallenge}-Back Challenge
                    </h1>
                    <button
                        onClick={handleGoBack}
                        disabled={gameState === 'preparing'} // Disable if starting
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Return to Dashboard"
                    >
                        ← Back to Dashboard
                    </button>
                </div>

                {/* Explanation Text Section */}
                <div className="space-y-4 text-base text-gray-700 mb-8">
                    <p>
                        Pass Level <strong className="font-semibold text-gray-900">{challengeLevelString}</strong> in one attempt to advance directly to <strong className="font-semibold text-gray-900">{nextNBackLevel}-Back</strong> training.
                    </p>
                    {/* Goal Information Box */}
                    <div className="bg-indigo-50 border border-indigo-200 p-3 rounded text-indigo-800 text-sm shadow-sm">
                        <strong className="font-semibold">Goal:</strong> Achieve at least <strong className='font-semibold'>"Good"</strong> performance (d-prime based) in this session. Success unlocks Level <strong className="font-semibold">{nextLevelString}</strong>!
                    </div>
                    <p className="text-sm text-gray-600 italic">
                        Note: This challenge attempt won't affect your regular statistics or session history.
                    </p>
                </div>

                {/* Action Buttons Section */}
                <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6 border-t border-gray-200">
                    {/* Back Button */}
                    <button
                        onClick={handleGoBack}
                        disabled={gameState === 'preparing'} // Disable if starting
                        className="px-5 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-base font-medium transition-colors shadow-sm w-full sm:w-auto order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Return to the dashboard without attempting the challenge"
                    >
                        Maybe Later
                    </button>
                    {/* Start Attempt Button */}
                    <button
                        onClick={handleStartAttempt}
                        className="px-6 py-3 bg-[#4338CA] text-white rounded-lg hover:bg-[#3730a3] font-medium text-base transition duration-150 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4338CA] flex items-center justify-center w-full sm:w-auto order-1 sm:order-2 disabled:opacity-75 disabled:cursor-not-allowed"
                        title={`Attempt the challenge at level ${challengeLevelString}`}
                        // Disable button briefly if game is already preparing/playing (prevents double clicks)
                        disabled={gameState === 'preparing' || gameState === 'playing'}
                    >
                        <FontAwesomeIcon icon={faPlay} className="mr-1.5 h-3.5 w-3.5" />
                        {gameState === 'preparing' ? 'Starting...' : 'Start Attempt'}
                    </button>
                </div>

            </div> {/* End Content Card */}
        </div> // End Page Container
    );
};

export default ChallengeExplanationScreen;